import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { DirEntry, ExifInfo, IsiZip } from '../../lib/api'
import { bytes } from '../../lib/format'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { bisaUnduhJpeg, jenisPratinjau, type SumberBerkas } from '../browser/source'
import { toast } from '../overlay'
import css from './PreviewViewer.module.css'

/**
 * Penampil pratinjau.
 *
 * Perbedaan penting dari versi lama: pratinjau punya alamatnya sendiri
 * (?preview=<path>). Dulu ia hanya keadaan di dalam JavaScript, sehingga
 * tombol Kembali peramban justru mengeluarkan pengguna dari folder, dan
 * pratinjau tidak bisa dibagikan sebagai tautan.
 */

interface Props {
  sumber: SumberBerkas
  item: DirEntry
  /** Berkas lain yang bisa dipratinjau, dalam urutan yang sedang TAMPIL. */
  daftar: DirEntry[]
  onTutup: () => void
  onPindah: (item: DirEntry) => void
}

export function PreviewViewer({ sumber, item, daftar, onTutup, onPindah }: Props) {
  const indeks = daftar.findIndex((d) => d.path === item.path)
  const sebelum = indeks > 0 ? daftar[indeks - 1] : undefined
  const sesudah = indeks >= 0 && indeks < daftar.length - 1 ? daftar[indeks + 1] : undefined
  const jenis = jenisPratinjau(item)

  const [exif, setExif] = useState<ExifInfo | null>(null)

  // ── Papan tik ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onTutup()
      } else if (e.key === 'ArrowLeft' && sebelum) {
        e.preventDefault()
        onPindah(sebelum)
      } else if (e.key === 'ArrowRight' && sesudah) {
        e.preventDefault()
        onPindah(sesudah)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onTutup, onPindah, sebelum, sesudah])

  // ── Kunci gulir halaman di belakang ──────────────────────────────────────
  useEffect(() => {
    const asli = document.body.style.cssText
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.cssText = asli
    }
  }, [])

  // ── EXIF ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (jenis !== 'gambar') {
      setExif(null)
      return
    }
    const ac = new AbortController()
    setExif(null)
    sumber
      .exif(item.path, ac.signal)
      .then(setExif)
      .catch(() => setExif(null))
    return () => ac.abort()
  }, [sumber, item.path, jenis])

  // ── Geser di ponsel ──────────────────────────────────────────────────────
  const sentuhX = useRef<number | null>(null)
  const geser = {
    onTouchStart: (e: React.TouchEvent) => {
      sentuhX.current = e.touches[0]?.clientX ?? null
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const awal = sentuhX.current
      const akhir = e.changedTouches[0]?.clientX
      sentuhX.current = null
      if (awal === null || akhir === undefined) return
      const jarak = akhir - awal
      // 50 px cukup jauh untuk membedakan geser dari sentuhan biasa.
      if (jarak > 50 && sebelum) onPindah(sebelum)
      else if (jarak < -50 && sesudah) onPindah(sesudah)
    },
  }

  const rincianExif = useMemo(() => {
    if (!exif) return []
    const baris: { label: string; nilai: string }[] = []
    if (exif.camera) baris.push({ label: 'Kamera', nilai: exif.camera })
    if (exif.lens) baris.push({ label: 'Lensa', nilai: exif.lens })
    if (exif.shutter) baris.push({ label: 'Rana', nilai: exif.shutter })
    if (exif.aperture) baris.push({ label: 'Bukaan', nilai: exif.aperture })
    if (exif.iso) baris.push({ label: 'ISO', nilai: exif.iso.replace('ISO ', '') })
    if (exif.focal) baris.push({ label: 'Fokal', nilai: exif.focal })
    if (exif.ev) baris.push({ label: 'Kompensasi', nilai: exif.ev })
    if (exif.width && exif.height) {
      baris.push({ label: 'Dimensi', nilai: `${exif.width} × ${exif.height}` })
    }
    if (exif.date) baris.push({ label: 'Diambil', nilai: exif.date })
    return baris
  }, [exif])

  return createPortal(
    <div className={css.backdrop} role="dialog" aria-modal="true" aria-label={`Pratinjau ${item.name}`}>
      <div className={css.topbar}>
        <Button variant="ghost" icon="arrowLeft" onClick={onTutup} aria-label="Tutup pratinjau" />

        <div className={css.title}>
          <div className={`${css.titleText} truncate`} title={item.name}>
            {item.name}
          </div>
          <div className={css.subtitle}>
            {bytes(item.size ?? 0)}
            {daftar.length > 1 && ` · ${indeks + 1} dari ${daftar.length}`}
          </div>
        </div>

        <div className={css.actions}>
          {/*
            HEIF terlihat wajar di sini karena yang ditampilkan adalah versi
            webp bikinan server. Berkas aslinya justru yang merepotkan —
            Photoshop lama tidak mengenalnya — jadi jalan keluarnya ditaruh
            tepat di tempat orang menyadari masalahnya.
          */}
          {sumber.kemampuan.bolehUnduh && sumber.urlJpeg && bisaUnduhJpeg(item) && (
            <Button
              variant="secondary"
              icon="image"
              onClick={() => {
                toast.info('Mengonversi ke JPEG, mohon tunggu sebentar.')
                window.location.href = sumber.urlJpeg?.(item.path) ?? '#'
              }}
            >
              JPEG
            </Button>
          )}

          {sumber.kemampuan.bolehUnduh && (
            <Button
              variant="secondary"
              icon="download"
              onClick={() => {
                window.location.href = sumber.urlUnduh(item.path)
              }}
            >
              <span className="sr-only">Unduh berkas</span>
            </Button>
          )}
        </div>
      </div>

      <div className={css.body} {...geser}>
        {daftar.length > 1 && (
          <>
            <button
              type="button"
              className={`${css.navButton} ${css.navPrev}`}
              onClick={() => sebelum && onPindah(sebelum)}
              disabled={!sebelum}
              aria-label="Berkas sebelumnya"
            >
              <Icon name="chevronLeft" size={22} />
            </button>
            <button
              type="button"
              className={`${css.navButton} ${css.navNext}`}
              onClick={() => sesudah && onPindah(sesudah)}
              disabled={!sesudah}
              aria-label="Berkas berikutnya"
            >
              <Icon name="chevronRight" size={22} />
            </button>
          </>
        )}

        <Penyaji sumber={sumber} item={item} jenis={jenis} />
      </div>

      {rincianExif.length > 0 && (
        <div className={css.exif}>
          {rincianExif.map((r) => (
            <span className={css.exifItem} key={r.label}>
              <span className={css.exifLabel}>{r.label}</span>
              <span className={css.exifValue}>{r.nilai}</span>
            </span>
          ))}
          {exif?.gps && (
            <a
              className={css.exifItem}
              href={`https://www.google.com/maps?q=${exif.gps.lat},${exif.gps.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={css.exifLabel}>Lokasi</span>
              <span className={css.exifValue}>
                {exif.gps.lat.toFixed(4)}, {exif.gps.lng.toFixed(4)}
              </span>
              <Icon name="external" size={12} />
            </a>
          )}
        </div>
      )}
    </div>,
    document.body,
  )
}

/** Penyaji per jenis berkas. */
function Penyaji({
  sumber,
  item,
  jenis,
}: {
  sumber: SumberBerkas
  item: DirEntry
  jenis: ReturnType<typeof jenisPratinjau>
}) {
  const [memuat, setMemuat] = useState(true)
  const [gagal, setGagal] = useState(false)
  const [teks, setTeks] = useState<string | null>(null)

  // Kunci berdasarkan path: mengganti berkas harus mengulang keadaan muat,
  // kalau tidak, berkas baru akan tampil dengan status berkas sebelumnya.
  useEffect(() => {
    setMemuat(true)
    setGagal(false)
    setTeks(null)
  }, [item.path])

  useEffect(() => {
    if (jenis !== 'teks') return
    const ac = new AbortController()

    fetch(sumber.urlLihat(item.path), { credentials: 'same-origin', signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status))
        return r.text()
      })
      // Dipotong agar berkas log berukuran ratusan megabyte tidak
      // membekukan tab peramban.
      .then((t) => setTeks(t.slice(0, 512 * 1024)))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setGagal(true)
      })
      .finally(() => setMemuat(false))

    return () => ac.abort()
  }, [sumber, item.path, jenis])

  if (gagal) {
    return (
      <div className={css.loading}>
        <Icon name="error" size={28} />
        Berkas ini tidak dapat ditampilkan.
      </div>
    )
  }

  switch (jenis) {
    case 'gambar':
      return (
        <>
          {memuat && <Memuat label="Memuat gambar" />}
          <img
            key={item.path}
            className={css.image}
            src={sumber.urlPratinjau(item.path)}
            alt={item.name}
            style={memuat ? { display: 'none' } : undefined}
            onLoad={() => setMemuat(false)}
            // Bila versi pratinjau gagal (mis. format tidak didukung sharp),
            // berkas aslinya dicoba sekali. Tanpa penanda, kegagalan kedua
            // akan memicu putaran tanpa henti.
            onError={(e) => {
              const el = e.currentTarget
              if (el.dataset.fallback) {
                setGagal(true)
                return
              }
              el.dataset.fallback = '1'
              el.src = sumber.urlLihat(item.path)
            }}
          />
        </>
      )

    case 'video':
      return (
        <video
          key={item.path}
          className={css.video}
          src={sumber.urlLihat(item.path)}
          controls
          autoPlay
          playsInline
          onLoadedData={() => setMemuat(false)}
          onError={() => setGagal(true)}
        />
      )

    case 'audio':
      return (
        <div className={css.audioWrap}>
          <div className={css.audioIcon}>
            <Icon name="fileAudio" size={30} />
          </div>
          <div className={css.audioName}>{item.name}</div>
          <audio
            key={item.path}
            src={sumber.urlLihat(item.path)}
            controls
            autoPlay
            style={{ width: '100%' }}
            onError={() => setGagal(true)}
          />
        </div>
      )

    case 'pdf':
      return (
        <iframe
          key={item.path}
          className={css.pdf}
          src={sumber.urlLihat(item.path)}
          title={item.name}
          // Sandbox tanpa allow-scripts: berkas PDF berasal dari NAS dan bisa
          // diunggah siapa saja yang punya akses tulis; menjalankan skripnya
          // pada origin kita sendiri adalah risiko yang tidak perlu diambil.
          sandbox=""
          onLoad={() => setMemuat(false)}
        />
      )

    case 'zip':
      return <PenyajiZip sumber={sumber} item={item} />

    case 'teks':
      if (memuat) return <Memuat label="Memuat teks" />
      // textContent lewat penyisipan React, bukan innerHTML — versi lama
      // merender README dengan innerHTML tanpa sanitasi, sehingga siapa pun
      // yang bisa menaruh berkas di NAS dapat menjalankan skrip di peramban
      // orang lain.
      return <pre className={css.text}>{teks}</pre>

    default:
      return (
        <div className={css.loading}>
          <Icon name="file" size={28} />
          Pratinjau tidak tersedia untuk tipe berkas ini.
        </div>
      )
  }
}

/**
 * Daftar isi arsip ZIP.
 *
 * Dibaca dari central directory saja — arsip 20 GB pun terbaca dalam
 * hitungan milidetik tanpa satu byte pun didekompresi.
 */
function PenyajiZip({ sumber, item }: { sumber: SumberBerkas; item: DirEntry }) {
  const [isi, setIsi] = useState<IsiZip[] | null>(null)
  const [gagal, setGagal] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    setIsi(null)
    setGagal(null)
    sumber
      .isiZip(item.path, ac.signal)
      .then(setIsi)
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setGagal(
          err instanceof Error ? err.message : 'Arsip ini tidak dapat dibaca.',
        )
      })
    return () => ac.abort()
  }, [sumber, item.path])

  if (gagal) {
    return (
      <div className={css.loading}>
        <Icon name="fileArchive" size={28} />
        {gagal}
      </div>
    )
  }

  if (!isi) return <Memuat label="Membaca isi arsip" />

  return (
    <div className={css.zip}>
      <div className={css.zipKepala}>
        {isi.length} berkas di dalam arsip · {bytes(isi.reduce((n, e) => n + e.size, 0))} setelah diekstrak
      </div>
      <div className={css.zipDaftar}>
        {isi.slice(0, 2000).map((e) => {
          // Indentasi mengikuti kedalaman folder, agar strukturnya terbaca.
          const dalam = e.name.split('/').filter(Boolean).length - 1
          const nama = e.name.split('/').filter(Boolean).pop() ?? e.name
          return (
            <div className={css.zipBaris} key={e.name}>
              <span style={{ paddingLeft: `${Math.min(dalam, 6) * 14}px` }} className="truncate">
                {nama}
              </span>
              <span className={css.zipUkuran}>{bytes(e.size)}</span>
            </div>
          )
        })}
        {isi.length > 2000 && (
          <div className={css.zipBaris}>
            <span>… dan {isi.length - 2000} berkas lainnya</span>
          </div>
        )}
      </div>
    </div>
  )
}

function Memuat({ label }: { label: string }) {
  return (
    <div className={css.loading}>
      <svg className={css.spinner} width="26" height="26" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      {label}…
    </div>
  )
}
